import { fetchApi } from "@/lib/api";
import { useCallback, useRef } from "react";
import { CurrentAISuggestion } from "@/components/conversations/ai-suggested-replies";
import { usePostHogEvent } from "@/lib/posthog";

interface AISuggestionInteractionData {
  text: string;
  approach?: string;
  triggeringMessageId: string | null;
  suggestion_type: "default" | "specific" | "priority";
  quoted_message_text?: string;
}

interface UseAISuggestionFeedbackProps {
  mentorshipId: string | undefined;
  userId: string | undefined;
}

interface FeedbackPayload {
  mentorship_id: string;
  user_id: string;
  last_message_id: string | null;
  suggested_reply: string;
  final_reply?: string;
  feedback_type: "accepted" | "edited" | "skipped" | "ignored";
  agent_name: string;
  suggestion_approach?: string;
  suggestion_type: "default" | "specific" | "priority";
  quoted_message_text?: string;
  all_suggestions?: { statement: string; question?: string };
}

export function useAISuggestionFeedback({
  mentorshipId,
  userId,
}: UseAISuggestionFeedbackProps) {
  // Ref to store the suggestion that the user has clicked on and is currently
  // considered "active" for feedback purposes.
  const activeSuggestionForFeedbackRef =
    useRef<AISuggestionInteractionData | null>(null);

  // PostHog event tracker
  const { dispatchEvent: trackAiEvent } = usePostHogEvent("ai_suggestion");

  // Function to submit the feedback to the backend
  const submitFeedback = useCallback(
    async (
      // Omit mentorship_id and user_id as they are added by the hook.
      // agent_name is now derived within this function.
      feedbackData: Omit<
        FeedbackPayload,
        "mentorship_id" | "user_id" | "agent_name"
      >,
    ) => {
      if (!mentorshipId || !userId) {
        console.warn(
          "AISuggestionFeedback: Missing mentorshipId or userId. Skipping feedback.",
        );
        return;
      }

      let agent_name = "reply_bot"; // Default
      if (feedbackData.suggestion_type === "default") {
        agent_name = "default";
      } else if (feedbackData.suggestion_type === "priority") {
        agent_name = "trigger_suggestion";
      } else if (feedbackData.suggestion_type === "specific") {
        agent_name = "specific_message_suggestion";
      }

      const payload: FeedbackPayload = {
        ...feedbackData,
        mentorship_id: mentorshipId,
        user_id: userId,
        agent_name,
      };

      try {
        await fetchApi({
          url: `v2/messages/ai-feedback`,
          method: "POST",
          data: payload,
        });
        // Track feedback event in PostHog
        trackAiEvent("ai_suggestion_feedback", {
          ...payload,
          feedback_type: payload.feedback_type,
          suggestion_type: payload.suggestion_type,
          agent_name: payload.agent_name,
          quoted_message_text: payload.quoted_message_text,
          all_suggestions: payload.all_suggestions,
          has_final_reply: Boolean(payload.final_reply),
          final_reply_length: payload.final_reply
            ? payload.final_reply.length
            : 0,
        });
      } catch (error) {
        console.error(
          "AISuggestionFeedback: Failed to submit feedback:",
          error,
          payload,
        );
      }
    },
    [mentorshipId, userId, trackAiEvent],
  );

  // Called when a user clicks on any suggestion in the UI
  const userClickedSuggestion = useCallback(
    (clickedData: AISuggestionInteractionData) => {
      if (
        activeSuggestionForFeedbackRef.current &&
        (activeSuggestionForFeedbackRef.current.text !== clickedData.text ||
          activeSuggestionForFeedbackRef.current.triggeringMessageId !==
            clickedData.triggeringMessageId ||
          activeSuggestionForFeedbackRef.current.suggestion_type !==
            clickedData.suggestion_type ||
          activeSuggestionForFeedbackRef.current.quoted_message_text !==
            clickedData.quoted_message_text)
      ) {
        submitFeedback({
          suggested_reply: activeSuggestionForFeedbackRef.current.text,
          feedback_type: "skipped",
          suggestion_approach: activeSuggestionForFeedbackRef.current.approach,
          last_message_id:
            activeSuggestionForFeedbackRef.current.triggeringMessageId,
          suggestion_type:
            activeSuggestionForFeedbackRef.current.suggestion_type,
          quoted_message_text:
            activeSuggestionForFeedbackRef.current.quoted_message_text,
        });
      }
      activeSuggestionForFeedbackRef.current = clickedData;
    },
    [submitFeedback],
  );

  // Called when the user actually sends a message
  const userSentMessage = useCallback(
    (finalMessageText: string): boolean => {
      if (activeSuggestionForFeedbackRef.current) {
        const {
          text: originalSuggestion,
          approach,
          triggeringMessageId,
          suggestion_type,
          quoted_message_text,
        } = activeSuggestionForFeedbackRef.current;

        if (finalMessageText === originalSuggestion) {
          submitFeedback({
            suggested_reply: originalSuggestion,
            feedback_type: "accepted",
            suggestion_approach: approach,
            last_message_id: triggeringMessageId,
            suggestion_type: suggestion_type,
            quoted_message_text: quoted_message_text,
          });
        } else {
          submitFeedback({
            suggested_reply: originalSuggestion,
            final_reply: finalMessageText,
            feedback_type: "edited",
            suggestion_approach: approach,
            last_message_id: triggeringMessageId,
            suggestion_type: suggestion_type,
            quoted_message_text: quoted_message_text,
          });
        }
        activeSuggestionForFeedbackRef.current = null;
        return true;
      }
      // If no suggestion was active, no feedback is sent from this function.
      return false;
    },
    [submitFeedback],
  );

  // Called when a user explicitly discards a VISIBLE suggestion,
  // typically by clicking its dedicated "refresh" icon.
  const userDiscardedVisibleSuggestion = useCallback(
    (discardedData: AISuggestionInteractionData) => {
      submitFeedback({
        suggested_reply: discardedData.text,
        feedback_type: "skipped",
        suggestion_approach: discardedData.approach,
        last_message_id: discardedData.triggeringMessageId,
        suggestion_type: discardedData.suggestion_type,
        quoted_message_text: discardedData.quoted_message_text,
      });

      if (
        activeSuggestionForFeedbackRef.current &&
        activeSuggestionForFeedbackRef.current.text === discardedData.text &&
        activeSuggestionForFeedbackRef.current.triggeringMessageId ===
          discardedData.triggeringMessageId &&
        activeSuggestionForFeedbackRef.current.suggestion_type ===
          discardedData.suggestion_type &&
        activeSuggestionForFeedbackRef.current.quoted_message_text ===
          discardedData.quoted_message_text
      ) {
        activeSuggestionForFeedbackRef.current = null;
      }
    },
    [submitFeedback],
  );

  // Called when the user clicks the global "New Approach" button,
  // indicating they want a completely new set of suggestions.
  const userRequestedNewApproach = useCallback(() => {
    if (activeSuggestionForFeedbackRef.current) {
      submitFeedback({
        suggested_reply: activeSuggestionForFeedbackRef.current.text,
        feedback_type: "skipped",
        suggestion_approach: activeSuggestionForFeedbackRef.current.approach,
        last_message_id:
          activeSuggestionForFeedbackRef.current.triggeringMessageId,
        suggestion_type: activeSuggestionForFeedbackRef.current.suggestion_type,
        quoted_message_text:
          activeSuggestionForFeedbackRef.current.quoted_message_text,
      });
      activeSuggestionForFeedbackRef.current = null;
    }
  }, [submitFeedback]);

  // Called when the user sends a message while AI suggestions are open but not used
  const userIgnoredAISuggestion = useCallback(
    ({
      suggestion,
      sentMessage,
    }: {
      suggestion: CurrentAISuggestion;
      sentMessage: string;
    }) => {
      let suggested_reply = "";
      let suggestion_type: "default" | "specific" | "priority" = "default";
      let quoted_message_text: string | undefined = undefined;
      let all_suggestions:
        | { statement: string; question?: string }
        | undefined = undefined;
      const last_message_id: string | null =
        suggestion.triggeringMessageId ?? null;
      if (suggestion.type === "default") {
        suggested_reply = suggestion.statement;
        suggestion_type = "default";
        quoted_message_text = suggestion.quoted_message_text;
        all_suggestions = {
          statement: suggestion.statement,
          question: suggestion.question,
        };
      } else if (
        suggestion.type === "specific" ||
        suggestion.type === "priority"
      ) {
        suggested_reply = suggestion.draft;
        suggestion_type = suggestion.type;
        quoted_message_text = suggestion.quoted_message_text;
      }
      submitFeedback({
        suggested_reply,
        final_reply: sentMessage,
        feedback_type: "ignored",
        suggestion_type,
        quoted_message_text,
        all_suggestions,
        last_message_id,
      });
    },
    [submitFeedback],
  );

  return {
    userClickedSuggestion,
    userSentMessage,
    userDiscardedVisibleSuggestion,
    userRequestedNewApproach,
    userIgnoredAISuggestion,
  };
}
